import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { ChevronLeft, Target, Shield, Heart, MapPin, CheckCircle, Users, Briefcase } from 'lucide-react';

const AboutPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const features = [
    t('about.features.1'),
    t('about.features.2'),
    t('about.features.3'),
    t('about.features.4'),
    t('about.features.5'),
    t('about.features.6'),
    t('about.features.7'),
    t('about.features.8'),
    t('about.features.9'),
    t('about.features.10'),
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-4 lg:px-8 h-16 flex items-center">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)}
          className="text-slate-600"
        >
          <ChevronLeft className="w-5 h-5 mr-1" />
          {t('about.back')}
        </Button>
      </header>

      {/* Hero */}
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl md:text-4xl font-bold mb-4">{t('about.title')}</h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto">
            {t('about.subtitle')}
          </p>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Mission */}
        <section className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <Target className="w-5 h-5 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900">{t('about.mission.title')}</h2>
          </div>
          <p className="text-slate-600 leading-relaxed text-lg">
            {t('about.mission.text')}
          </p>
        </section>

        {/* The Problem We Solve */}
        <section className="mb-12 bg-white rounded-2xl border border-slate-200 p-6 md:p-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">{t('about.problem.title')}</h2>
          <p className="text-slate-600 leading-relaxed mb-6">
            {t('about.problem.intro')}
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3 p-4 bg-red-50 rounded-xl">
              <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-red-600 text-sm font-bold">1</span>
              </div>
              <p className="text-slate-700">{t('about.problem.1')}</p>
            </div>
            <div className="flex items-start gap-3 p-4 bg-red-50 rounded-xl">
              <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-red-600 text-sm font-bold">2</span>
              </div>
              <p className="text-slate-700">{t('about.problem.2')}</p>
            </div>
            <div className="flex items-start gap-3 p-4 bg-red-50 rounded-xl">
              <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-red-600 text-sm font-bold">3</span>
              </div>
              <p className="text-slate-700">{t('about.problem.3')}</p>
            </div>
            <div className="flex items-start gap-3 p-4 bg-red-50 rounded-xl">
              <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-red-600 text-sm font-bold">4</span>
              </div>
              <p className="text-slate-700">{t('about.problem.4')}</p>
            </div>
          </div>
          <p className="text-slate-600 leading-relaxed mt-6">
            {t('about.problem.solution').split('R25').map((part, idx) => 
              idx === 0 ? (
                <span key={idx}>{part}<span className="font-bold text-emerald-600">R25</span></span>
              ) : part
            )}
          </p>
        </section>

        {/* Our Values */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">{t('about.values.title')}</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">{t('about.values.honesty.title')}</h3>
              <p className="text-slate-600 text-sm">
                {t('about.values.honesty.text')}
              </p>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-4">
                <Heart className="w-6 h-6 text-emerald-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">{t('about.values.affordable.title')}</h3>
              <p className="text-slate-600 text-sm">
                {t('about.values.affordable.text')}
              </p>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-4">
                <MapPin className="w-6 h-6 text-amber-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">{t('about.values.local.title')}</h3>
              <p className="text-slate-600 text-sm">
                {t('about.values.local.text')}
              </p>
            </div>
          </div>
        </section>

        {/* What You Get */}
        <section className="mb-12 bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-6 md:p-8 text-white">
          <h2 className="text-2xl font-bold mb-6">{t('about.features.title')}</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {features.map((feature, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                <span className="text-slate-200">{feature}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Stats */}
        <section className="mb-12">
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white rounded-2xl border border-slate-200 p-6 text-center">
              <Briefcase className="w-8 h-8 text-slate-400 mx-auto mb-2" />
              <p className="text-3xl font-bold text-slate-900">6</p>
              <p className="text-sm text-slate-500">{t('about.stats.templates')}</p>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 p-6 text-center">
              <Users className="w-8 h-8 text-slate-400 mx-auto mb-2" />
              <p className="text-3xl font-bold text-slate-900">77+</p>
              <p className="text-sm text-slate-500">{t('about.stats.profiles')}</p>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 p-6 text-center">
              <MapPin className="w-8 h-8 text-slate-400 mx-auto mb-2" />
              <p className="text-3xl font-bold text-slate-900">9</p>
              <p className="text-sm text-slate-500">{t('about.stats.provinces')}</p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="text-center bg-emerald-50 rounded-2xl border border-emerald-200 p-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-3">{t('about.cta.title')}</h2>
          <p className="text-slate-600 mb-6 max-w-lg mx-auto">
            {t('about.cta.text')}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button 
              onClick={() => navigate('/register')}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-8"
            >
              {t('about.cta.button')}
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate('/login')}
              className="border-slate-300"
            >
              {t('about.cta.login')}
            </Button>
          </div>
        </section>

        {/* Contact */}
        <section className="mt-12 text-center">
          <h2 className="text-xl font-bold text-slate-900 mb-3">{t('about.contact.title')}</h2>
          <p className="text-slate-600">
            {t('about.contact.text')} <a href="mailto:support@careerstep.co.za" className="text-emerald-600 font-medium hover:underline">support@careerstep.co.za</a>
          </p>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 py-8 px-4 mt-12">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-sm">{t('footer.copyright')}</p>
          <div className="flex gap-6 text-sm">
            <button onClick={() => navigate('/terms')} className="text-slate-500 hover:text-slate-900">{t('footer.terms')}</button>
            <button onClick={() => navigate('/privacy')} className="text-slate-500 hover:text-slate-900">{t('footer.privacy')}</button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
